import React, { useContext, useEffect, useState, ChangeEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { ValidatorForm, TextValidator, SelectValidator } from 'react-material-ui-form-validator';
import { ScorecardCreationQuestionViewModel } from './Viewmodels/ScorecardCreationViewModel';
import { APIContext } from '../Contexts/APIContext';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';

type Props = {
    open: boolean;
    editId: number;
    sectionId: number;
    onClose: (refresh: boolean) => void;
}

export default function AddEditScorecardQuestionModal({ open, editId, sectionId, onClose }: Props) {
    const { get, post } = useContext(APIContext);
    const [question, setQuestion] = useState(new ScorecardCreationQuestionViewModel(sectionId));

    useEffect(() => {
        if (editId > 0 && open) getData();
        else setQuestion(new ScorecardCreationQuestionViewModel(sectionId));
    }, [editId, open]);

    const getData = () => {
        get('ScorecardManagement', `GetQuestionContent?questionId=${editId}`, (data) => {
            setQuestion(data);
        });
    }

    const saveQuestion = () => {
        post('ScorecardManagement', `AddEditScorecardQuestion`, question, (data) => {
            onClose(true);
        });
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuestion({
            ...question,
            [e.target.name]: e.target.value
        });
    }

    const handleCheckedChange = (e: ChangeEvent<HTMLInputElement>) => {
        setQuestion({
            ...question,
            [e.target.name]: e.target.checked
        });
    }

    const remove = () => {
        get('ScorecardManagement', `RemoveQuestion?questionId=${editId}`, (data) => {
            onClose(true);
        });
    }

    return (
        <Dialog
            open={open}
            onClose={() => onClose(false)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>{editId > 0 ? "Edit " : "Add "} Scorecard Question</DialogTitle>
            <ValidatorForm onSubmit={saveQuestion}>
                <DialogContent>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Grid container spacing={1}>
                                <Grid size={12}>
                                    <InputLabel shrink>Question Text</InputLabel>
                                    <TextValidator
                                        name='questionText'
                                        fullWidth
                                        value={question.questionText}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Type</InputLabel>
                                    <SelectValidator
                                        name='type'
                                        fullWidth
                                        value={question.type}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                    >
                                        <MenuItem key={0} value="RichText">RichText</MenuItem>
                                        <MenuItem key={1} value="TextDropdown">TextDropdown</MenuItem>
                                        <MenuItem key={2} value="Dropdown">Dropdown</MenuItem>
                                        <MenuItem key={3} value="Subheading">Subheading</MenuItem>
                                    </SelectValidator>
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Category</InputLabel>
                                    <TextValidator
                                        name='category'
                                        fullWidth
                                        value={question.category}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Sub Category</InputLabel>
                                    <TextValidator
                                        name='subCategory'
                                        fullWidth
                                        value={question.subCategory}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Client Category</InputLabel>
                                    <TextValidator
                                        name='clientCategory'
                                        fullWidth
                                        value={question.clientCategory}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                        validators={['required']}
                                        errorMessages={['This field is required']}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Description</InputLabel>
                                    <TextValidator
                                        name='description'
                                        fullWidth
                                        value={question.description}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={6}>
                            <Grid container spacing={1}>
                                <Grid size={12}>
                                    <InputLabel shrink>Dependency JSON</InputLabel>
                                    <TextValidator
                                        name='dependencyJSON'
                                        fullWidth
                                        value={question.dependencyJSON}
                                        onChange={handleChange}
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        size="small"
                                        margin="none"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Custom JSON</InputLabel>
                                    <TextValidator
                                        name='customJSON'
                                        fullWidth
                                        value={question.customJSON}
                                        onChange={handleChange}
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        size="small"
                                        margin="none"
                                        validators={question.type !== 'RichText' ? ['required'] : []}
                                        errorMessages={question.type !== 'RichText' ?['This field is required'] : []}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Dependency Tag</InputLabel>
                                    <TextValidator
                                        name='dependencyTag'
                                        fullWidth
                                        value={question.dependencyTag}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Dependent Questions</InputLabel>
                                    <TextValidator
                                        name='dependentQuestions'
                                        fullWidth
                                        value={question.dependentQuestions}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Group Question Flag</InputLabel>
                                    <Switch name='groupQuestion' onChange={handleCheckedChange} checked={question.groupQuestion} />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Critical Fail</InputLabel>
                                    <Switch name='criticalFail' onChange={handleCheckedChange} checked={question.criticalFail ?? undefined} />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Outcome Flag</InputLabel>
                                    <Switch name='outcomeFlag' onChange={handleCheckedChange} checked={question.outcomeFlag} />
                                </Grid>
                                <Grid size={12}>
                                    <InputLabel shrink>Tenure Flag</InputLabel>
                                    <Switch name='tenureFlag' onChange={handleCheckedChange} checked={question.tenureFlag} />
                                </Grid>
                            </Grid>
                        </Grid>

                    </Grid>
                    <DialogActions>
                        <Button onClick={() => onClose(false)}>Cancel</Button>
                        {editId > 0 && <Button color="error" onClick={remove}>Remove</Button>}
                        <Button variant="contained" type="submit">Save</Button>
                    </DialogActions>
                </DialogContent>
            </ValidatorForm>
        </Dialog>
    );
}